import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Banknote, CreditCard, Landmark, TrendingUp, TrendingDown, ArrowRight, Building2, ChevronRight, Wallet } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { format, subMonths } from 'date-fns';
import ShareViewButton from '@/components/common/ShareViewButton';

export default function DetailedReportPage() {
  const navigate = useNavigate();
  const { branches, fetchBranches } = useBranchStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const f = useReportFilters({ initialRange: searchParams.get('start') ? { start: searchParams.get('start'), end: searchParams.get('end') || searchParams.get('start') } : undefined });
  const dateRange = f.range;
  const branchFilter = f.branchIds;

  useEffect(() => { fetchBranches(); /* eslint-disable-next-line */ }, []);

  // Mirror filter state into URL so ShareViewButton copies a fully hydratable link.
  useEffect(() => {
    setSearchParams({ start: dateRange.start, end: dateRange.end }, { replace: true });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [dateRange]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...f.params, limit: 5000 };
        const [sR, eR] = await Promise.allSettled([
          api.get('/sales', { params }),
          api.get('/expenses', { params }),
        ]);
        if (sR.status === 'fulfilled') setSales(sR.value.data?.data || sR.value.data || []);
        if (eR.status === 'fulfilled') setExpenses(eR.value.data?.data || eR.value.data || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchData();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [f.params]);

  // Sales breakdown
  const salesBreakdown = useMemo(() => {
    let cash = 0, bank = 0, credit = 0, online = 0, total = 0;
    const byBranch = {};

    sales.forEach(s => {
      const br = branches.find(b => b.id === s.branch_id)?.name || 'Unassigned (no branch)';
      if (!byBranch[br]) byBranch[br] = { cash: 0, bank: 0, credit: 0, online: 0, total: 0, id: s.branch_id };
      (s.payment_details || []).forEach(p => {
        const a = p.amount || 0;
        if (p.mode === 'cash') { cash += a; byBranch[br].cash += a; }
        else if (p.mode === 'bank') { bank += a; byBranch[br].bank += a; }
        else if (p.mode === 'credit') { credit += a; byBranch[br].credit += a; }
        else { online += a; byBranch[br].online += a; }
      });
      const amt = s.amount || 0;
      total += amt;
      byBranch[br].total += amt;
    });
    return { cash, bank, credit, online, total, byBranch };
  }, [sales, branches]);

  // Expenses breakdown
  const expBreakdown = useMemo(() => {
    let cash = 0, bank = 0, credit = 0, total = 0;
    const byBranch = {};
    const byCategory = {};

    expenses.forEach(e => {
      const br = branches.find(b => b.id === e.branch_id)?.name || 'Unassigned (no branch)';
      if (!byBranch[br]) byBranch[br] = { cash: 0, bank: 0, credit: 0, total: 0, id: e.branch_id };
      const cat = e.category || e.sub_category || 'Other';
      if (!byCategory[cat]) byCategory[cat] = { cash: 0, bank: 0, credit: 0, total: 0 };

      const a = e.amount || 0;
      if (e.payment_mode === 'cash') { cash += a; byBranch[br].cash += a; byCategory[cat].cash += a; }
      else if (e.payment_mode === 'bank') { bank += a; byBranch[br].bank += a; byCategory[cat].bank += a; }
      else { credit += a; byBranch[br].credit += a; byCategory[cat].credit += a; }
      total += a;
      byBranch[br].total += a;
      byCategory[cat].total += a;
    });

    // Sort categories by total descending
    const sortedCats = Object.entries(byCategory).sort((a, b) => b[1].total - a[1].total);
    return { cash, bank, credit, total, byBranch, byCategory: sortedCats };
  }, [expenses, branches]);

  const netCash = salesBreakdown.cash - expBreakdown.cash;
  const netBank = salesBreakdown.bank - expBreakdown.bank;

  const goToExpenses = (branchId) => {
    const params = new URLSearchParams();
    if (branchId) params.set('branch', branchId);
    if (dateRange.start) params.set('date', dateRange.start);
    navigate(`/expenses?${params.toString()}`);
  };

  const goToSales = (branchId) => {
    const params = new URLSearchParams();
    if (branchId) params.set('branch', branchId);
    if (dateRange.start) params.set('date', dateRange.start);
    navigate(`/sales?${params.toString()}`);
  };

  const Amt = ({ v, color = 'text-foreground' }) => (
    <span className={`font-semibold inline-flex items-center gap-0.5 ${color}`}>
      <RiyalIcon size="0.65em" /> {(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="detailed-report-page">
        <ReportHeader icon={Wallet} title={<span data-testid="page-title">Detailed Report</span>} subtitle="Cash, Bank & Credit breakdown for Sales and Expenses" testId="detailed-header"
          actions={<ShareViewButton params={{ start: dateRange.start, end: dateRange.end }} />} />
        <ReportFilterBar testId="detailed-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds} range={f.range} onRange={f.setRange}>
          <ExportButtons branchIds={branchFilter} dataType="sales" label="Cash & Bank Report" startDate={dateRange?.start} endDate={dateRange?.end} />
        </ReportFilterBar>

        {/* Net Position Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Banknote} label="Net Cash" value={netCash} color="emerald" testId="net-cash" />
          <StatCard icon={Landmark} label="Net Bank" value={netBank} color="blue" testId="net-bank" />
          <StatCard icon={CreditCard} label="Credit Given" value={salesBreakdown.credit} color="amber" testId="credit-given" />
          <StatCard icon={CreditCard} label="Credit Taken" value={expBreakdown.credit} color="red" testId="credit-taken" />
        </div>

        {/* Sales Breakdown */}
        <Card data-testid="sales-breakdown">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp size={16} className="text-success" /> Sales Income</CardTitle>
              <Badge variant="outline" className="text-success border-success/30 bg-success/10"><Amt v={salesBreakdown.total} color="text-success" /></Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <ModeCard label="Cash Sales" value={salesBreakdown.cash} icon={Banknote} color="emerald" />
              <ModeCard label="Bank Sales" value={salesBreakdown.bank} icon={Landmark} color="blue" />
              <ModeCard label="Credit Sales" value={salesBreakdown.credit} icon={CreditCard} color="amber" />
              <ModeCard label="Online Sales" value={salesBreakdown.online} icon={TrendingUp} color="purple" />
            </div>
            {/* By Branch */}
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">By Branch</h4>
            <div className="space-y-2">
              {Object.entries(salesBreakdown.byBranch).sort((a, b) => b[1].total - a[1].total).map(([name, d]) => (
                <div key={name} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-border hover:bg-muted/50 cursor-pointer transition-all group"
                  onClick={() => goToSales(d.id)} data-testid={`sales-branch-${name}`}>
                  <div className="flex items-center gap-2.5">
                    <Building2 size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{name}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-success">Cash: <Amt v={d.cash} color="text-success" /></span>
                    <span className="text-info">Bank: <Amt v={d.bank} color="text-info" /></span>
                    <span className="text-warning">Credit: <Amt v={d.credit} color="text-warning" /></span>
                    <Badge variant="outline"><Amt v={d.total} /></Badge>
                    <ChevronRight size={14} className="text-muted-foreground group-hover:text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Expenses Breakdown */}
        <Card data-testid="expenses-breakdown">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><TrendingDown size={16} className="text-error" /> Expenses Spent</CardTitle>
              <Badge variant="outline" className="text-error border-error/30 bg-error/10"><Amt v={expBreakdown.total} color="text-error" /></Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <ModeCard label="Cash Spent" value={expBreakdown.cash} icon={Banknote} color="red" />
              <ModeCard label="Bank Spent" value={expBreakdown.bank} icon={Landmark} color="orange" />
              <ModeCard label="Credit Spent" value={expBreakdown.credit} icon={CreditCard} color="stone" />
            </div>

            {/* By Category */}
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Where Money Was Spent</h4>
            <div className="space-y-2 mb-6">
              {expBreakdown.byCategory.map(([cat, d]) => (
                <div key={cat} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-border hover:bg-muted/50 cursor-pointer transition-all group"
                  onClick={() => goToExpenses()} data-testid={`exp-cat-${cat}`}>
                  <span className="text-sm font-medium">{cat}</span>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-success">Cash: <Amt v={d.cash} color="text-success" /></span>
                    <span className="text-info">Bank: <Amt v={d.bank} color="text-info" /></span>
                    <span className="text-warning">Credit: <Amt v={d.credit} color="text-warning" /></span>
                    <Badge variant="outline"><Amt v={d.total} /></Badge>
                    <ChevronRight size={14} className="text-muted-foreground group-hover:text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>

            {/* By Branch */}
            <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">By Branch</h4>
            <div className="space-y-2">
              {Object.entries(expBreakdown.byBranch).sort((a, b) => b[1].total - a[1].total).map(([name, d]) => (
                <div key={name} className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-border hover:bg-muted/50 cursor-pointer transition-all group"
                  onClick={() => goToExpenses(d.id)} data-testid={`exp-branch-${name}`}>
                  <div className="flex items-center gap-2.5">
                    <Building2 size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium">{name}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-success">Cash: <Amt v={d.cash} color="text-success" /></span>
                    <span className="text-info">Bank: <Amt v={d.bank} color="text-info" /></span>
                    <span className="text-warning">Credit: <Amt v={d.credit} color="text-warning" /></span>
                    <Badge variant="outline"><Amt v={d.total} /></Badge>
                    <ChevronRight size={14} className="text-muted-foreground group-hover:text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, testId }) {
  const isNeg = value < 0;
  const colorMap = {
    emerald: 'from-emerald-500 to-teal-600', blue: 'from-blue-500 to-indigo-600',
    amber: 'from-amber-500 to-orange-600', red: 'from-red-500 to-rose-600',
  };
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorMap[color]} flex items-center justify-center`}>
            <Icon size={18} className="text-white" />
          </div>
          <div>
            <div className={`text-lg font-bold inline-flex items-center gap-0.5 ${isNeg ? 'text-error' : 'text-foreground'}`}>
              <RiyalIcon size="0.6em" /> {Math.abs(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[11px] text-muted-foreground">{label}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ModeCard({ label, value, icon: Icon, color }) {
  const colors = {
    emerald: 'bg-success/10 text-success border-success/30',
    blue: 'bg-info/10 text-info border-info/30',
    amber: 'bg-warning/10 text-warning border-warning/30',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    red: 'bg-error/10 text-error border-error/30',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    stone: 'bg-muted/50 text-foreground border-border',
  };
  return (
    <div className={`rounded-xl border p-3.5 ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold inline-flex items-center gap-0.5">
        <RiyalIcon size="0.6em" /> {(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}
